/**
 * GroupConversationThread - Group chat thread view: header, message list, and composer.
 */

import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { selectUser } from '../../store/auth.reducer';
import {
  getGroupConversation,
  getGroupMessages,
  markGroupConversationRead,
  sendGroupMessage,
} from '../../helpers/messagesHelpers';
import { getUserDailyPointStats, getAuraMessageCounts } from '../../helpers/auraHelpers';
import { useSocket } from '../../utils/SocketContext';
import { useMessageCount } from '../../utils/MessageCountContext';
import useSwipeToClose from '../../hooks/useSwipeToClose';
import MessageAuraPill from './MessageAuraPill';
import UserAvatar from '../Profile/UserAvatar';

function GroupConversationThread({ groupConversationId, groupConversation, onBack, onSettings }) {
  const currentUser = useSelector(selectUser);
  const { subscribe } = useSocket();
  const { refreshUnreadMessageCount } = useMessageCount();
  const [messages, setMessages] = useState([]);
  const [auraCounts, setAuraCounts] = useState({});
  const [dailyStats, setDailyStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [conv, setConv] = useState(groupConversation);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  // Swipe-back only when the thread is rendered inline by the `/messages`
  // route. Inside `MessagesOverlay`, the surrounding `SlideOverlay` already
  // owns the gesture, so `onBack` is omitted there.
  const swipeBackEnabled = typeof onBack === 'function';
  const { ref: panelRef, dragX } = useSwipeToClose({
    open: true,
    direction: 'right',
    onClose: onBack,
    enabled: swipeBackEnabled,
  });
  const dragging = dragX !== 0;
  const panelStyle = dragging ? { transform: `translateX(${dragX}px)` } : undefined;
  const transitionClass = dragging ? 'transition-none' : 'transition-transform duration-300 ease-zen';

  useEffect(() => {
    let cancelled = false;
    getGroupMessages(groupConversationId)
      .then((data) => {
        if (!cancelled) {
          setMessages(Array.isArray(data) ? data : []);
          refreshUnreadMessageCount();
        }
      })
      .catch(() => {
        if (!cancelled) setMessages([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [groupConversationId, refreshUnreadMessageCount]);

  useEffect(() => {
    let cancelled = false;
    getUserDailyPointStats()
      .then((s) => { if (!cancelled) setDailyStats(s); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const others = messages.filter((m) => !m.is_own);
    if (others.length === 0) {
      setAuraCounts({});
      return;
    }
    let cancelled = false;
    getAuraMessageCounts(others.map((m) => ({ type: 'group_message', id: m.message_id })))
      .then((counts) => {
        if (!cancelled) setAuraCounts(counts.group_message || {});
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [messages]);

  useEffect(() => {
    if (!conv) {
      let cancelled = false;
      getGroupConversation(groupConversationId)
        .then((data) => { if (!cancelled) setConv(data); })
        .catch(() => {});
      return () => { cancelled = true; };
    }
    return undefined;
  }, [groupConversationId, conv]);

  useEffect(() => {
    const unsubscribe = subscribe('new_group_message', (payload) => {
      if (payload.group_conversation_id === groupConversationId) {
        const m = payload.message;
        const isOwn = m.sender_id === currentUser?.user_id;
        setMessages((prev) => [
          ...prev,
          {
            ...m,
            is_own: isOwn,
            displayname: m.displayname || currentUser?.displayname,
            username: m.username || currentUser?.username,
          },
        ]);
        if (!isOwn) {
          markGroupConversationRead(groupConversationId)
            .then(() => refreshUnreadMessageCount())
            .catch(() => {});
        }
      }
    });
    return unsubscribe;
  }, [subscribe, groupConversationId, currentUser?.user_id, currentUser?.displayname, currentUser?.username, refreshUnreadMessageCount]);

  useEffect(() => {
    if (scrollRef.current?.scrollIntoView) {
      scrollRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    const trimmed = input.trim();
    if (!trimmed || sending) return;
    setSending(true);
    try {
      const msg = await sendGroupMessage(groupConversationId, trimmed);
      setMessages((prev) => [
        ...prev,
        {
          ...msg,
          is_own: true,
          displayname: currentUser?.displayname,
          username: currentUser?.username,
        },
      ]);
      setInput('');
    } catch {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const displayName = conv?.display_name || 'Group';
  const avatarUser = {
    user_id: conv?.group_conversation_id,
    username: conv?.display_name,
    displayname: displayName,
    profile_pic_url: conv?.avatar_url,
    default_profile_color: conv?.default_profile_color,
  };

  return (
    <div
      ref={panelRef}
      style={panelStyle}
      className={`flex-1 flex flex-col min-h-0 ${transitionClass}`}
      data-testid="group-conversation-thread"
    >
      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <p className="text-silver text-sm">Loading...</p>
        </div>
      ) : (
      <>
      <div className="flex-shrink-0 flex items-center gap-3 p-4 border-b border-shadow bg-charcoal">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="text-ether text-sm hover:underline"
          >
            ← Back
          </button>
        )}
        <UserAvatar
          user={avatarUser}
          className="w-10 h-10 rounded-full"
          emojiClassName="text-base"
          alt={displayName}
        />
        <span className="font-medium text-ash flex-1 truncate">{displayName}</span>
        {dailyStats != null && (
          <span className="text-silver text-xs flex-shrink-0" title="Daily aura remaining">
            ✦ {dailyStats.remainingToday}/{dailyStats.dailyLimit}
          </span>
        )}
        {onSettings && (
          <button
            type="button"
            onClick={onSettings}
            className="p-2 rounded-lg text-silver hover:text-ash hover:bg-shadow transition-colors"
            title="Group settings"
            aria-label="Group settings"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </button>
        )}
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.map((m) => (
          <div
            key={m.message_id}
            className={`flex ${m.is_own ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[75%] flex items-end gap-1 ${m.is_own ? 'flex-row-reverse' : ''}`}
            >
              <div
                className={`px-4 py-2 rounded-2xl ${
                  m.is_own ? 'bg-ether text-void' : 'bg-shadow text-ash'
                }`}
              >
                {!m.is_own && (
                  <p className="text-xs text-silver mb-0.5">{m.displayname || m.username}</p>
                )}
                <p className="text-sm break-words">{m.content}</p>
              </div>
              {!m.is_own && (
                <MessageAuraPill
                  messageId={m.message_id}
                  messageType="group_message"
                  senderId={m.sender_id}
                  auraCount={auraCounts[m.message_id] ?? 0}
                  onAuraGiven={(points = 1) => {
                    setAuraCounts((prev) => ({
                      ...prev,
                      [m.message_id]: (prev[m.message_id] ?? 0) + points,
                    }));
                    getUserDailyPointStats().then(setDailyStats).catch(() => {});
                  }}
                />
              )}
            </div>
          </div>
        ))}
        <div ref={scrollRef} />
      </div>
      <form onSubmit={handleSend} className="sticky bottom-0 flex-shrink-0 p-4 border-t border-shadow bg-charcoal">
        <div className="flex gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Type a message..."
            className="flex-1 px-4 py-3 rounded-xl bg-shadow border border-charcoal text-ash placeholder-silver focus:outline-none focus:ring-2 focus:ring-ether/50"
          />
          <button
            type="submit"
            disabled={sending || !input.trim()}
            className="px-4 py-3 rounded-xl bg-ether text-void font-medium disabled:opacity-50"
          >
            Send
          </button>
        </div>
      </form>
      </>
      )}
    </div>
  );
}

export default GroupConversationThread;
